import { useState, useEffect, useMemo, useCallback } from "react";
import { Dropdown } from "antd";
import type { MenuProps } from "antd";
import "./customStyle.css";
import { LoadingOutlined } from "@ant-design/icons";
import {
  LessonToConfirmFragment,
  StudyPlanContentFragment,
  useHeaConfirmLessonMutation,
} from "@api/graphql-codegen";
import styles from "./LessonForm.module.scss";
import { Lesson } from "../../../types";
import NormalCheckIcon from "../../../assets/icons/mark_check_normal.svg?react";
import MarkCheckIcon from "../../../assets/icons/mark_check.svg?react";
import CheckIcon from "../../../assets/icons/complete_check.svg?react";
import CloseIcon from "../../../assets/icons/complete_uncheck.svg?react";
import TicketIcon from "src/assets/icons/ticket.svg?react";
import BurgerIcon from "src/assets/icons/burger.svg?react";
import ReviewImg from "src/assets/img/review.svg?react";
import { getMediaAbsolutePath } from "../../../services/File";
import Button from "@src/components/button/Button";
import { useToast } from "@src/hooks";
import { useMediaQuery } from "react-responsive";

type Props = {
  lesson: Lesson;
  index: number;
  openDetails: (lesson: Lesson) => void;
  openPreview: (lesson: Lesson, studyPlan: StudyPlanContentFragment) => void;
  unitId: string;
  isParentApp?: boolean;
  isLocked?: boolean;
  studyPlansForSelectedUnit?: StudyPlanContentFragment[];
  studyPlan?: StudyPlanContentFragment;
  openLessonWithChilds?: (lesson: Lesson) => void;
  studyMode?: string;
  isParentUnit?: boolean;
  movedScroll?: boolean;
  openExitTicket: (lesson: Lesson, studyPlan: StudyPlanContentFragment) => void;
  isReview?: boolean;
  refetch?: (complete: boolean) => void;
  incompleted?: boolean;
  reviewNow?: (lesson: Lesson) => void;
  reviewLesson?: (lesson: Lesson, studyPlan: StudyPlanContentFragment) => void;
  child: LessonToConfirmFragment;
};

function LessonCompleteForm(props: Props) {
  const {
    lesson,
    openDetails,
    openPreview,
    isLocked = false,
    studyPlan,
    movedScroll,
    openExitTicket,
    isReview = false,
    refetch,
    incompleted,
    reviewNow,
    reviewLesson,
    child,
  } = props;
  const { successToast } = useToast();
  const { name, image, duration, descriptionHeading, descriptionAnswers } =
    lesson;

  const [box, setBox] = useState<string[]>([]);
  const [isHovered, setIsHovered] = useState(false);
  const [review, setReview] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showCompleteOptionMenu, setShowCompleteOptionMenu] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 800 });

  const [heaConfirmLesson, { loading }] = useHeaConfirmLessonMutation();
  useEffect(() => {
    if (movedScroll) {
      setShowMenu(false);
      setShowCompleteOptionMenu(false);
    }
  }, [movedScroll]);

  useEffect(() => {
    if (incompleted) {
      setReview(true);
    } else {
      setReview(false);
    }
    if (descriptionAnswers) {
      setBox(JSON.parse(descriptionAnswers || "[]"));
    }
  }, [descriptionAnswers, incompleted]);

  const handleOpenPreview = () => {
    setShowMenu(false);
    setShowCompleteOptionMenu(false);
    if (studyPlan) {
      openPreview(lesson, studyPlan);
    }
  };

  const handleStartLesson = () => {
    setShowMenu(false);
    setShowCompleteOptionMenu(false);
    openDetails(lesson);
  };
  const studyPlanSubViewStyle = useMemo(() => {
    const classes = [styles.studyPlanSubView];
    if (incompleted) {
      if (isHovered) {
        classes.push(styles.hoverBorder);
      } else {
        classes.push(styles.reviewBorder);
      }
    }
    if (isHovered) {
      classes.push(styles.hoverBorder);
    }
    return classes.join(" ");
  }, [incompleted, isHovered]);

  const connectLineStyle = useMemo(() => {
    const classes = [styles.connectLine];

    if (incompleted) {
      if (isHovered) {
        classes.push(styles.hoverBorder);
      } else {
        classes.push(styles.reviewBorder);
      }
    }
    if (isHovered) {
      classes.push(styles.hoverBorder);
    }

    return classes.join(" ");
  }, [isHovered, incompleted]);
  const topBorderStyle = useMemo(() => {
    const classes = [styles.cardTopBorderView];

    if (isHovered) {
      classes.push(styles.hoverBorder);
    }

    return classes.join(" ");
  }, [isHovered]);

  const bottomBorderStyle = useMemo(() => {
    const classes = [styles.cardBottomBorderView];

    if (isHovered) {
      classes.push(styles.hoverBorder);
    }

    return classes.join(" ");
  }, [isHovered]);

  const imageStyle = useMemo(() => {
    const classes = [styles.image];

    return classes.join(" ");
  }, []);

  const completedCheckStyle = useMemo(() => {
    const classes = [styles.completedCheck];

    if (isHovered) {
      classes.push(styles.hoverBorder);
    }
    return classes.join(" ");
  }, [isHovered]);

  const viewExitTicketStyle = useMemo(() => {
    const classes = [styles.exitTicketButton];

    if (incompleted) {
      classes.push(styles.completeMenuItem);
    }
    return classes.join(" ");
  }, [incompleted]);

  const completeOptionMenuitemStyle = useMemo(() => {
    const classes = [styles.completeOptionMenuitem];

    if (incompleted) {
      classes.push(styles.completeMenuItem);
    }
    return classes.join(" ");
  }, [incompleted]);

  const completeButtonStyle = useMemo(() => {
    const classes = [styles.completeButton];
    if (incompleted) {
      classes.push(styles.completeMenuItem);
    }
    return classes.join(" ");
  }, [incompleted]);

  const handleHover = useCallback((status: boolean) => {
    setIsHovered(status);
  }, []);

  const markLessonAsComplete = useCallback(
    async (e: any) => {
      if (e.domEvent) {
        e.domEvent.stopPropagation();
      } else {
        e.stopPropagation();
      }
      setShowCompleteOptionMenu(false);
      if (!studyPlan?.id) {
        return;
      }
      const res = await heaConfirmLesson({
        variables: {
          input: {
            studyplanId: studyPlan.id,
            lessonId: lesson.id,
            completed: true,
          },
        },
      });
      if (res?.data?.heaConfirmLesson) {
        refetch?.(true);
        successToast("Successfully updated Lesson as Complete");
        // Do not fire lesson_completed here — already tracked on Completed/Confirmed.
      }
    },
    [
      lesson,
      studyPlan?.id,
      heaConfirmLesson,
      refetch,
      successToast,
    ],
  );

  const handleLessonInComplete = (e: any) => {
    if (e.domEvent) {
      e.domEvent.stopPropagation();
    } else {
      e.stopPropagation();
    }
    setShowMenu(false);
    setShowCompleteOptionMenu(false);
    if (lesson && studyPlan) {
      reviewLesson?.(lesson, studyPlan);
    }
  };

  const handleVisible = useCallback((visible: boolean) => {
    setShowMenu(visible);
    setShowCompleteOptionMenu(false);
  }, []);

  const handleExitTicketActiveVisible = useCallback((visible: boolean) => {
    setShowMenu(false);
    setShowCompleteOptionMenu(visible);
  }, []);

  const handleOpenExitTicket = (e: any) => {
    if (e.domEvent) {
      e.domEvent.stopPropagation();
    } else {
      e.stopPropagation();
    }
    setShowMenu(false);
    setShowCompleteOptionMenu(false);
    if (studyPlan) {
      openExitTicket(lesson, studyPlan);
    }
  };

  const handleReviewNow = (e: any) => {
    if (e.domEvent) {
      e.domEvent.stopPropagation();
    } else {
      e.stopPropagation();
    }
    setShowMenu(false);
    setShowCompleteOptionMenu(false);
    reviewNow?.(lesson);
  };

  const items: MenuProps["items"] = isReview
    ? [
        {
          key: "1",
          label: !isLocked && (
            <div
              className={styles.dropDownMenuitem}
              onClick={handleOpenPreview}
            >
              Preview Lesson
            </div>
          ),
        },
        {
          key: "2",
          label: !isLocked && (
            <div
              className={styles.dropDownMenuitem}
              onClick={handleStartLesson}
            >
              Start Lesson
            </div>
          ),
        },
      ]
    : [
        {
          key: "1",
          label: !isLocked && (
            <div
              className={styles.dropDownMenuitem}
              onClick={handleOpenPreview}
            >
              Preview Lesson
            </div>
          ),
        },
      ];

  const handleClickActiveMenu = (e: any) => {
    if (e.domEvent) {
      e.domEvent.stopPropagation();
    } else {
      e.stopPropagation();
    }
  };

  const normalMenu: MenuProps["items"] = [
    {
      key: "sub1",
      label: (
        <div className={styles.incompleteOptionMenuitem}>
          <CloseIcon /> {"Incomplete"}
        </div>
      ),
      onClick: (e) => handleLessonInComplete(e),
    },
    {
      key: "sub2",
      label: (
        <div className={completeOptionMenuitemStyle}>
          <CheckIcon /> {"Complete"}
        </div>
      ),
      onClick: (e) => markLessonAsComplete(e),
    },
  ];

  const reviewMenu: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <div className={styles.reviewOptionMenuitem}>{"View Exit Ticket"}</div>
      ),
      onClick: (e) => handleOpenExitTicket(e),
    },
    {
      key: "2",
      label: <div className={styles.reviewOptionMenuitem}>{"Complete?"}</div>,
      children: normalMenu,
      onClick: (e) => handleClickActiveMenu(e),
      onTitleClick: (e) => handleClickActiveMenu(e),
    },
    {
      key: "3",
      label: <div className={styles.reviewOptionMenuitem}>{"Review Now"}</div>,
      onClick: (e) => handleReviewNow(e),
    },
  ];

  return (
    <div
      className={`${styles.lessonContainer} ${review ? styles.inReview : ""}`}
    >
      <div className={styles.studyPlanView}>
        <div
          className={studyPlanSubViewStyle}
          onMouseOver={() => handleHover(true)}
          onMouseLeave={() => handleHover(false)}
        >
          <div
            className={styles.subItem}
            style={{ background: `${studyPlan?.darkColor}` }}
          >
            {child.studyplanName.substring(0, 1).toLocaleUpperCase()}
          </div>
          <div className={styles.planName}>{child.studyplanName}</div>
        </div>
      </div>
      <div className={styles.connectView}>
        <div className={connectLineStyle}></div>
      </div>
      <Dropdown
        menu={{ items }}
        trigger={["contextMenu", "click"]}
        onOpenChange={handleVisible}
        open={showMenu}
      >
        <div
          className={isHovered ? styles.hoverNewCard : styles.newCard}
          onMouseOver={() => handleHover(true)}
          onMouseLeave={() => handleHover(false)}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={topBorderStyle}></div>
          <div className={bottomBorderStyle}></div>
          <div
            className={imageStyle}
            style={{
              backgroundImage: `url(${image ? getMediaAbsolutePath(image) : ""})`,
              position: "relative",
            }}
          >
            {review ? (
              <div className={styles.previewBanner}>
                <ReviewImg />
              </div>
            ) : (
              <NormalCheckIcon
                className={styles.neednormalCheckIcon}
                width={34}
                height={34}
              />
            )}
          </div>
          <section className={styles.content}>
            <div className={styles.head}>
              <div className={styles.title}>{name}</div>
              <div className={styles.duration}>
                {duration ? `${duration}m` : ""}
              </div>
            </div>
            <div className={styles.description}>
              <div className={styles.subheading}>
                {descriptionHeading ?? "This lesson helps students:"}
              </div>
              <ul className={styles.boxView}>
                {(box ?? []).map((content, index) => {
                  const optionStr: string = content as string;
                  return (
                    <li key={index} className={styles.questionBox}>
                      {optionStr}
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className={styles.buttonsView}>
              {review ? (
                <>
                  <Dropdown
                    menu={{ items: reviewMenu }}
                    trigger={["click"]}
                    placement="bottomRight"
                    onOpenChange={handleExitTicketActiveVisible}
                    open={showCompleteOptionMenu}
                  >
                    <Button
                      label={""}
                      size="small"
                      onClick={(e) => e.stopPropagation()}
                      leftIcon={<BurgerIcon />}
                    />
                  </Dropdown>
                </>
              ) : (
                <>
                  <Button
                    label={isMobile ? "" : "View Exit Ticket"}
                    size="small"
                    className={viewExitTicketStyle}
                    onClick={handleOpenExitTicket}
                    leftIcon={isMobile ? <TicketIcon /> : undefined}
                  />
                  <Dropdown
                    menu={{ items: normalMenu }}
                    trigger={["click"]}
                    placement="bottomRight"
                    onOpenChange={handleExitTicketActiveVisible}
                    open={showCompleteOptionMenu}
                  >
                    <Button
                      label={isMobile ? "" : "Completed?"}
                      size="small"
                      className={completeButtonStyle}
                      onClick={(e) => e.stopPropagation()}
                      leftIcon={isMobile ? <CheckIcon /> : undefined}
                    />
                  </Dropdown>
                </>
              )}
            </div>
          </section>
          {
            <div className={styles.statusView}>
              <button
                disabled={isLocked}
                className={completedCheckStyle}
                onClick={(e) => e.stopPropagation()}
              >
                {loading ? (
                  <LoadingOutlined
                    style={{ fontSize: 24, borderRadius: 15 }}
                    spin
                  />
                ) : review ? (
                  <>
                    <MarkCheckIcon
                      className={styles.reviewCheckIcon}
                      width={32}
                      height={32}
                    />
                    <NormalCheckIcon
                      className={styles.inCompletedNormalIcon}
                      width={32}
                      height={32}
                    />
                  </>
                ) : (
                  <NormalCheckIcon
                    className={styles.normalCheckIcon}
                    width={32}
                    height={32}
                  />
                )}
              </button>
            </div>
          }
        </div>
      </Dropdown>
    </div>
  );
}

export default LessonCompleteForm;
